import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Music, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { staffApi, apiError } from "../lib/api";

export default function StaffLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const { loginStaff } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { user } = await loginStaff({ email, password });
      navigate(user.role === "super_admin" ? "/super" : "/staff/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    try {
      await staffApi.post("/auth/forgot-password", { email });
      setInfo("If that email is registered, a reset link is on its way.");
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#0B192C] text-[#FAF7EE] flex flex-col" data-testid="staff-login-page">
      <header className="px-5 py-4">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="back-to-home">
          <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
          <span className="font-display font-bold text-lg">ChoirQuest</span>
        </Link>
      </header>

      <main className="flex-1 flex items-start justify-center px-5 py-10">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{forgot ? "Reset your password" : "Staff sign in"}</h1>
          <p className="text-sm text-[#FAF7EE]/60 mt-1">
            {forgot ? "We'll email you a reset link." : "For choir directors, teachers and institution administrators."}
          </p>

          <form onSubmit={forgot ? sendReset : submit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="staff-email" className="text-xs font-bold uppercase tracking-widest text-[#FAF7EE]/60">Email</label>
              <input id="staff-email" type="email" required data-testid="staff-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border-2 border-[#1E3A5F] bg-[#12253E] px-4 py-3 focus:border-[#C69B3D] focus:outline-none min-h-[52px]" />
            </div>
            {!forgot && (
              <div>
                <label htmlFor="staff-password" className="text-xs font-bold uppercase tracking-widest text-[#FAF7EE]/60">Password</label>
                <div className="relative mt-1">
                  <input id="staff-password" type={showPw ? "text" : "password"} required data-testid="staff-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border-2 border-[#1E3A5F] bg-[#12253E] px-4 py-3 pr-12 focus:border-[#C69B3D] focus:outline-none min-h-[52px]" />
                  <button type="button" onClick={() => setShowPw(!showPw)} aria-label="Toggle password visibility"
                    data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FAF7EE]/50 hover:text-[#C69B3D] min-h-[44px] min-w-[44px] flex items-center justify-center">
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}
            {error && <p role="alert" data-testid="staff-login-error" className="text-sm text-[#FBF0F2] bg-[#A62B2B]/30 border border-[#A62B2B] rounded-xl px-4 py-3">{error}</p>}
            {info && <p role="status" data-testid="staff-login-info" className="text-sm text-[#EBF6F0] bg-[#2D6A4F]/30 border border-[#2D6A4F] rounded-xl px-4 py-3">{info}</p>}
            <button type="submit" disabled={loading} data-testid="staff-login-submit"
              className="w-full px-6 py-4 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors disabled:opacity-50 min-h-[52px]">
              {loading ? "Signing in…" : forgot ? "Send reset link" : "Sign in"}
            </button>
          </form>

          <div className="mt-5 flex items-center justify-between text-sm">
            <button type="button" onClick={() => { setForgot(!forgot); setError(""); setInfo(""); }}
              data-testid="forgot-password-toggle" className="text-[#C69B3D] underline min-h-[44px]">
              {forgot ? "Back to sign in" : "Forgot password?"}
            </button>
            <Link to="/play" className="text-[#FAF7EE]/60 underline min-h-[44px] flex items-center" data-testid="chorister-login-link">
              Chorister login
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
